import { erpApi } from "@/shared/axios";
import { useMutation, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { urlBackend } from '@/shared/constants/networking.constants';
import { getUrlParamsFetch } from "@/shared";
import { OrdenTrabajoNoc, OrdenTrabajoNocFilter, UbicacionProductoFilter } from "@/shared/interfaces/erp/noc/orden-trabajo-noc.interface";

const { post, get, put, remove } = erpApi();

const BASE_URL_NOC = `${urlBackend}api/v1/noc/`;

/**
 * Hook mutation para obtener los nodos
 */
export const useGetNodo = () => {
  return useMutation({
    mutationFn: async (params: any) => {
      const queryParams = getUrlParamsFetch(params || {});
      const resp = await get<any[]>(
        `${urlBackend}api/v1/nodo?${queryParams}`,
        params,
        true
      );
      return resp;
    },
  });
};

/**
 * Hook mutation para obtener las flotas por usuario
 */
export const useGetFlotaByUser = (username: string) => {
  return useQuery({
    queryKey: ['flota', username],
    queryFn: async () => {
      const resp = await get<any[]>(
        `${urlBackend}api/v1/flota/?user=${username}`,
        true
      );
      return resp;
    },
    enabled: !!username, // evita ejecutar si no hay username
  });
};

/**
 * Hook mutation para obtener la ubicacion del nodo -> codido nodo es el numero de referencia en ubicacion
 */
export const useGetUbicacionByCodeNode = (codenode: string) => {
  return useQuery({
    queryKey: ['ubicacion', codenode],
    queryFn: async () => {
      const resp = await get<any[]>(
        `${urlBackend}api/v1/ubicacion/?page_size=1&estado=true&codigo=${codenode}`,
        true
      );
      return resp;
    },
    enabled: !!codenode, // evita ejecutar si no hay codenode
  });
};

/**
 * Hook mutation para obtener los productos por ubicación
 */
export const useGetUbicacionProducto = async (params?: UbicacionProductoFilter) => {
  const queryParams = getUrlParamsFetch(params || {});
  const resp = get<any[]>(
    `${urlBackend}api/v1/ubicacion-producto/?${queryParams}`,
    true
  );
  return resp;
};

export const useFetchUbicacionProducto = (params: UbicacionProductoFilter, options?: Omit<UseQueryOptions<any, Error, any>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: ['ubicacion-producto', params],
    queryFn: async () => {
      return await useGetUbicacionProducto(params)
    },
    refetchOnWindowFocus: false,
    ...options,
  })
};


/**
 * Hook mutation para obtener el tipo de actividad
 */
export const useGetActivityType = () => {
  return useMutation({
    mutationFn: async (params: any) => {
      const queryParams = getUrlParamsFetch(params || {});
      const resp = await get<any[]>(
        `${BASE_URL_NOC}activity-type?${queryParams}`,
        params,
        true
      );
      return resp;
    },
  });
};

export const useFetchActivityTypeById = (params: { idActivityType?: string }, options?: any) => {
  return useQuery({
    queryKey: ['activityType', ...Object.values(params || {})],
    queryFn: async () => {
      return await useGetActivityTypeById(params)
    },
    enabled: !!params.idActivityType,
    refetchOnWindowFocus: false,
    ...options,
  })
};

export const useGetActivityTypeById = async (params?: any) => {
  const resp = get<OrdenTrabajoNoc>(
    `${BASE_URL_NOC}activity-type/${params?.idActivityType}`,
    true
  );
  return resp;
};

export const useCreateActivityType = () => {
  return useMutation({
    mutationFn: async (params: any) => {
      const resp = await post<any[]>(
        `${BASE_URL_NOC}activity-type`,
        params,
        true
      );
      return resp;
    },
  });
};

export const useUpdateActivityType = (id: any) => {
  return useMutation({
    mutationFn: async (params: any) => {
      const resp = await put<any[]>(
        `${BASE_URL_NOC}activity-type/${id}`,
        params,
        true
      );
      return resp;
    },
  });
};

/**
 * Hook mutation para obtener el tipo de actividad
 */
export const useGetIncidents = () => {
  return useMutation({
    mutationFn: async (params: any) => {
      const queryParams = getUrlParamsFetch(params || {});
      const resp = await get<any[]>(
        `${BASE_URL_NOC}incidents?${queryParams}`,
        params,
        true
      );
      return resp;
    },
  });
};

export const useCreateIncidents = () => {
  return useMutation({
    mutationFn: async (params: any) => {
      const resp = await post<any[]>(
        `${BASE_URL_NOC}incidents`,
        params,
        true
      );
      return resp;
    },
  });
};

/**
 * Hook mutation para obtener la orden de trabajo
 */
export const useFetchWorkOrder = (params: OrdenTrabajoNocFilter, options?: Omit<UseQueryOptions<any, Error, any>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: ['workOrder', params],
    queryFn: async () => {
      return await useGetWorkOrder(params)
    },
    refetchOnWindowFocus: false,
    ...options,
  })
};

export const useGetWorkOrder = async (params?: OrdenTrabajoNocFilter) => {
  const queryParams = getUrlParamsFetch(params || {});
  const resp = get<OrdenTrabajoNoc>(
    `${BASE_URL_NOC}work-order?${queryParams}`,
    true
  );
  return resp;
};

export const useFetchWorkOrderById = (params: { workOrderId?: string }, options?: any) => {
  return useQuery({
    queryKey: ['workOrder', params.workOrderId],
    queryFn: async () => {
      return await useGetWorkOrderById(params)
    },
    enabled: !!params.workOrderId,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    ...options,
  })
};

export const useGetWorkOrderById = async (params?: any) => {
  const resp = get<OrdenTrabajoNoc>(
    `${BASE_URL_NOC}work-order/${params?.workOrderId}`,
    true
  );
  return resp;
};

/**
 * Hook mutation para registrar la orden de trabajo
 */
export const useCreateWorkOrder = () => {
  return useMutation({
    mutationFn: async (params: any) => {
      const resp = await post<any[]>(
        `${BASE_URL_NOC}work-order`,
        params,
        true
      );
      return resp;
    },
  });
};

/**
 * Hook mutation para registrar la orden de trabajo
 */
export const useUpdateWorkOrder = (id: any) => {
  return useMutation({
    mutationFn: async (params: any) => {
      const resp = await put<any[]>(
        `${BASE_URL_NOC}work-order/${id}`,
        params,
        true
      );
      return resp;
    },
  });
};

// export const useDownloadPdf = (id: any) => {
//   window.open(`${BASE_URL_NOC}work-order/pdf/${id}`, '_blank');
// }

export const useDownloadPdf = () => {
  return useMutation({
    mutationFn: async (id: any) => {
      const resp = await get<Blob>(
        `${BASE_URL_NOC}work-order/pdf/${id}`,
        true,
        false
      );

      return resp;
    },
  });
};

/**
 * Hook mutation para registrar la orden de trabajo
 */
export const useCreatePhoto = () => {
  return useMutation({
    mutationFn: async (formData: FormData) => {
      return await post(
        `${BASE_URL_NOC}photos-reports`,
        formData,
        true,
      );
    },
  });
};

export const useDeletePhoto = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const resp = await remove<any[]>(
        `${BASE_URL_NOC}photos-reports/${id}`,
        true
      );

      return resp;
    },
  });
};


export const useCreateAuditReview = () => {
  return useMutation({
    mutationFn: async (params: any) => {
      const resp = await post<any[]>(
        `${BASE_URL_NOC}audit-review`,
        params,
        true
      );
      return resp;
    },
  });
};